import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchStatsDataSet } from '@/store/slices/ownerHubStats';
import { isoDay, parseDay, type DateRange } from '@/nova/pages/affiliates/reports-helpers';
import { groupDimension, groupMetrics, type Metrics } from '@/nova/pages/home/stats-shape';

import { campaignGroups, matchedCampaignMetrics } from './video-stats';

/**
 * Stats for a set of tracking-link `campaign` values (see `tracking-links-api.ts`): one grouped read of the Owners Hub
 * Stats `daily_affiliate_activity` data set over the range, aggregated on `campaign`, keeping the groups whose campaign
 * value is one of the given ones. Signup stores each link's `campaign` segment verbatim, so the group's echoed value IS
 * the link's token. Sum the result with `videoStatValue` / `sumMetricField` (`video-stats.ts`).
 *
 * Shared by every surface that shows link performance: an asset's own links, a landing page's combined links, a
 * library card. Only the newest read lands (a request id guards fast range or set changes).
 */
export type CampaignStatsStatus = 'loading' | 'no-brand' | 'none' | 'error' | 'ok';

export interface CampaignStats {
  status: CampaignStatsStatus;
  /** One metric object per matched campaign group. */
  metricsList: Metrics[];
  /** The same groups keyed by their campaign value (a per-link breakdown reads it). */
  byCampaign: ReadonlyMap<string, Metrics>;
  error?: string;
  reload: () => void;
}

export interface UseCampaignStatsInput {
  /** The links' `campaign` values; empty = `none` (no read). */
  campaigns: readonly string[];
  range: DateRange;
}

export const CAMPAIGN_STATS_DATA_SET = 'daily_affiliate_activity';

const NO_GROUPS: ReadonlyMap<string, Metrics> = new Map();

/** The groups of a `campaign`-grouped answer whose campaign is one of `keys`, keyed by it (one group per campaign:
 *  the answer is grouped on `campaign` alone). Pure. */
const matchedByCampaign = (groups: readonly unknown[], keys: ReadonlySet<string>): Map<string, Metrics> => {
  const out = new Map<string, Metrics>();
  for (const group of groups) {
    const campaign = groupDimension(group, 'campaign');
    if (campaign !== undefined && keys.has(campaign)) out.set(campaign, groupMetrics(group));
  }
  return out;
};

/** The stats window is end-EXCLUSIVE (`start <= row < end`): include the selected `to` day by asking for the day after
 *  it. Falls back to the raw value when it cannot be parsed. */
export const endExclusive = (to: string): string => {
  const d = parseDay(to);
  if (Number.isNaN(d.getTime())) return to;
  d.setDate(d.getDate() + 1);
  return isoDay(d);
};

export function useCampaignStats({ campaigns, range }: UseCampaignStatsInput): CampaignStats {
  const dispatch = useAppDispatch();
  const casinoInstanceId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  const [reloadTick, setReloadTick] = React.useState(0);
  const [state, setState] = React.useState<Omit<CampaignStats, 'reload'>>({
    status: 'loading',
    metricsList: [],
    byCampaign: NO_GROUPS,
  });
  const reqIdRef = React.useRef(0);
  const { from, to } = range;
  const campaignsKey = [...campaigns].filter(Boolean).sort().join('|');

  // Syncs with the network: the grouped stats read for the current set and range.
  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    const isStale = (): boolean => reqId !== reqIdRef.current;
    if (!casinoInstanceId) {
      setState({ status: 'no-brand', metricsList: [], byCampaign: NO_GROUPS });
      return;
    }
    const keys = new Set(campaignsKey ? campaignsKey.split('|') : []);
    if (keys.size === 0) {
      setState({ status: 'none', metricsList: [], byCampaign: NO_GROUPS });
      return;
    }
    setState((prev) => ({ ...prev, status: 'loading', error: undefined }));
    void dispatch(
      fetchStatsDataSet({
        casinoInstanceId,
        dataSet: CAMPAIGN_STATS_DATA_SET,
        startDate: from,
        endDate: endExclusive(to),
        aggregateOn: ['campaign'],
      }),
    )
      .unwrap()
      .then((entry) => {
        if (isStale()) return;
        const groups = campaignGroups(entry.data);
        setState({
          status: 'ok',
          metricsList: matchedCampaignMetrics(groups, keys),
          byCampaign: matchedByCampaign(groups, keys),
        });
      })
      .catch((err: unknown) => {
        if (isStale()) return;
        // A thunk refusal is `{ error }`, not an Error.
        const refusal = (err as { error?: unknown } | null)?.error;
        const error = typeof refusal === 'string' ? refusal : err instanceof Error ? err.message : String(err);
        setState({ status: 'error', metricsList: [], byCampaign: NO_GROUPS, error });
      });
  }, [casinoInstanceId, campaignsKey, from, to, reloadTick, dispatch]);

  const reload = React.useCallback(() => setReloadTick((tick) => tick + 1), []);
  return { ...state, reload };
}

export default useCampaignStats;
